import React, { useCallback, useEffect } from "react";

import { getProgramInternalName, useAddOrUpdateProgramParticipation } from "utils";

import { CarbonProgram, CarbonProgramParticipation, CarbonProgramParticipationRequest } from "models";

import { Col, Form, InputNumber, Modal, Option, Row, Select, TextArea, Message as message } from "components";

export type AssignCarbonProgramModalProps = {
  programParticipation: CarbonProgramParticipation | null;
  carbonPrograms: CarbonProgram[];
  showModal: boolean;
  onClose: () => void;
};

export const AssignCarbonProgramModal = ({ programParticipation, carbonPrograms, showModal, onClose }: AssignCarbonProgramModalProps) => {
  const { mutate: addOrUpdateProgramParticipation, isPending, isSuccess, isError, reset } = useAddOrUpdateProgramParticipation();

  const [form] = Form.useForm();

  const handleSubmit = (v: Required<Omit<CarbonProgramParticipationRequest, "farmId">>) => {
    if (!programParticipation) {
      return;
    }
    addOrUpdateProgramParticipation({
      farmId: programParticipation.id,
      carbonProgramId: v.carbonProgramId,
      haLimit: +v.haLimit,
      comment: v.comment,
      tags: v.tags.map((tag) => tag.toUpperCase()),
    });
  };

  // Get the accounting methodology of the selected farm
  const selectedProgramAccountingMethodology = carbonPrograms.find((carbonProgram) => carbonProgram.id === programParticipation?.carbonProgramId)
    ?.accountingMethodology;

  // Get the corresponding program list or pass all the carbon programs if the methodology does not matched
  const programList = selectedProgramAccountingMethodology
    ? carbonPrograms.filter((carbonProgram) => carbonProgram.accountingMethodology === selectedProgramAccountingMethodology)
    : carbonPrograms;

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  useEffect(() => {
    // This resets the initial values when the modal is opened for another farm
    form.resetFields();
  }, [programParticipation?.id, form]);

  useEffect(() => {
    if (isSuccess) {
      message({
        content: "The gremlins have successfully added or updated the program",
        icon: "gremlin",
      });

      handleClose();
    }

    if (isError) {
      message({
        content: "There was an error while adding or updating the program",
        icon: "gremlin",
      });
    }
  }, [isSuccess, isError, reset, handleClose]);

  return (
    <Modal
      showModal={showModal}
      onCancel={handleClose}
      onOk={() => form.submit()}
      okText="Submit"
      loading={isPending}
      destroyOnHidden={true}
      content={
        <Form form={form} onFinish={handleSubmit}>
          <Row>
            <Col>
              <>
                {/* @ts-expect-error need to change */}
                <Select
                  dataTestId="program-participation-type-select"
                  label={programParticipation?.carbonProgramId ? "Carbon programs currently cannot be changed" : "Select carbon program"}
                  popupMatchSelectWidth={false}
                  name="carbonProgramId"
                  rules={[{ required: true }]}
                  initialValue={programParticipation?.carbonProgramId}
                >
                  {Object.values(programList).map((program, i) => (
                    <Option data-testid={`program-participation-type-select-option-${i}`} value={program.id} key={i}>
                      {getProgramInternalName(program)}
                    </Option>
                  ))}
                </Select>
              </>
            </Col>
          </Row>

          <Row>
            <Col>
              <InputNumber
                dataTestId="program-participation-max-hectares-input"
                label="Hectare limit"
                min={0}
                step={0}
                name="haLimit"
                initialValue={programParticipation?.maxHectares}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextArea
                dataTestId="program-participation-comment-text-area"
                label="Comment (max. 155)"
                name="comment"
                maxLength={155}
                initialValue={programParticipation?.comment}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              {/* @ts-expect-error need to change */}
              <Select dataTestId="program-participation-tag-select" name="tags" mode="tags" label="Tags" initialValue={programParticipation?.tags ?? []}>
                {(programParticipation?.tags ?? []).map((tag, index) => (
                  <Option data-testid={`program-participation-tags-option-${index}`} value={tag} key={index}>
                    {tag.toUpperCase()}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Form>
      }
    />
  );
};
